// LC 진단테스트(입회) 어드민 API. 모든 id는 string(BigInt 안전).
import { apiClient, ApiResponse } from './client'
import type { DiagnosticStatus } from './learnerDiagnostics'

export interface DiagnosticAssignmentRow {
  id: string
  userId: number
  status: DiagnosticStatus
  aiqQuizSetId: number
  levelQuizSetId: number
  aiqSolved: number
  aiqTotal: number
  roundNo: number
  aiqCarried: boolean
  levelCarried: boolean
  levelGraded?: boolean
}

export interface SetRecommendation {
  id: number
  name: string
  levelValue: string
}

export interface RecommendResponse {
  aiqSet: SetRecommendation | null
  levelSet: SetRecommendation | null
}

export interface QuizSetSummary {
  id: number
  name: string
  levelType: string | null
  levelValue: string | null
  /** 학기로 나뉜 레벨 테스트 세트만 1·2. 서버가 null이면 필드를 생략한다. */
  semester?: number | null
}

export interface LevelQuizItem {
  seq: number
  quizId: string
  title: string | null
}

export interface LevelGradeItem {
  seq: number
  quizId: string
  isCorrect: boolean
}

export interface LevelGradeResponse {
  correct: number
  total: number
  score: number
}

// ── 결과 조회 ────────────────────────────────────────────────────────────────
export interface AiqResultItem {
  seq: number
  quizId: string
  isCorrect: boolean | null
  partialCredit: number | null
  elapsedSec: number | null
  elapsedMs: number | null
}

export interface LevelResultItem {
  seq: number
  quizId: string
  isCorrect: boolean
}

export interface DiagnosticResultResponse {
  status: DiagnosticStatus
  aiq: { correct: number; total: number; items: AiqResultItem[] }
  level: { correct: number; total: number; score: number; items: LevelResultItem[] } | null
}

// ── 진단 리포트 (배점가중 레벨채점 + 과정결정, spec §4) ────────────────────────
export interface DiagnosticReportBasic {
  nickName: string
  gradeLabel: string
  gradeLevel?: string | null
  diagnosedAt: string | null
  levelTimeLimitMin?: number | null
  levelElapsedMin?: number | null
  aiqTimeLimitMin?: number | null
  aiqElapsedMin?: number | null
}

export interface DiagnosticReportLevelItem {
  no: number
  difficulty: '상' | '중' | '하'
  isCorrect: boolean
  hint: string | null
  /** 표시용 보완점: 전체를 감싼 대괄호는 서버가 뗀 값 */
  checkPoint: string | null
  /** 레벨 테스트 영역(hint 선행 분류). 분류 없는 문항은 null */
  area?: string | null
}

/** 레벨 테스트 영역별 결과. 세트 문항이 가진 영역만 내려온다. */
export interface DiagnosticReportLevelArea {
  name: string
  correctCount: number
  itemCount: number
}

export interface DiagnosticReportLevel {
  totalScore: number
  itemCount: number
  band: 'HIGH' | 'MID' | 'LOW'
  firstCourse: 'A' | 'B' | 'C' | 'D' | 'E' | null
  template: string | null // 'template-1'..'template-7'
  progressScore: number | null
  items: DiagnosticReportLevelItem[]
  areas?: DiagnosticReportLevelArea[]
}

export interface DiagnosticReportAiq {
  aiqScore: number
  aiqRank: number | null
  band: 'HIGH' | 'MID' | 'LOW'
}

export interface DiagnosticReportRecommendedCourse {
  course: 'A' | 'B' | 'C' | 'D' | 'E' | null
  needsAiq: boolean
  reasonKey?:
    | 'PRESCHOOL_BELOW_THRESHOLD'
    | 'PRESCHOOL_PASS'
    | `${'A' | 'B' | 'C' | 'D' | 'E'}_${'HIGH' | 'MID' | 'LOW'}`
    | null
}

export interface DiagnosticReportResponse {
  basic: DiagnosticReportBasic
  level: DiagnosticReportLevel | null
  aiq: DiagnosticReportAiq | null
  recommendedCourse: DiagnosticReportRecommendedCourse | null
}

export const adminDiagnosticsApi = {
  list: (status?: DiagnosticStatus) =>
    apiClient.get<ApiResponse<DiagnosticAssignmentRow[]>>('/lc/admin/diagnostics', {
      params: status ? { status } : undefined,
    }),

  recommend: (userId: number) =>
    apiClient.get<ApiResponse<RecommendResponse>>('/lc/admin/diagnostics/recommend', {
      params: { userId },
    }),

  assign: (body: { userId: number; aiqQuizSetId: number; levelQuizSetId: number }) =>
    apiClient.post<ApiResponse<DiagnosticAssignmentRow>>('/lc/admin/diagnostics', body),

  update: (id: string, body: { aiqQuizSetId?: number; levelQuizSetId?: number; cancel?: boolean }) =>
    apiClient.put<ApiResponse<null>>(`/lc/admin/diagnostics/${id}`, body),

  getLevel: (id: string) =>
    apiClient.get<ApiResponse<{ items: LevelQuizItem[] }>>(`/lc/admin/diagnostics/${id}/level`),

  grade: (id: string, body: { grades: LevelGradeItem[] }) =>
    apiClient.post<ApiResponse<LevelGradeResponse>>(`/lc/admin/diagnostics/${id}/level/grade`, body),

  getResult: (id: string) =>
    apiClient.get<ApiResponse<DiagnosticResultResponse>>(`/lc/admin/diagnostics/${id}/result`),

  getReport: (id: string) =>
    apiClient.get<ApiResponse<DiagnosticReportResponse>>(`/lc/admin/diagnostics/${id}/report`),

  resetAiq: (id: string) =>
    apiClient.post<ApiResponse<null>>(`/lc/admin/diagnostics/${id}/aiq/reset`),

  resetLevel: (id: string) =>
    apiClient.post<ApiResponse<null>>(`/lc/admin/diagnostics/${id}/level/reset`),

  retake: (
    id: string,
    body: { aiq: boolean; level: boolean; aiqQuizSetId?: number; levelQuizSetId?: number },
  ) => apiClient.post<ApiResponse<DiagnosticAssignmentRow>>(`/lc/admin/diagnostics/${id}/retake`, body),

  // 세트 선택용 — 목록 endpoint는 quizId 미포함이라 일반 JSON 안전.
  // 세트 상세는 quizSet.ts의 getQuizSet(BigInt-safe) 사용.
  listQuizSets: () =>
    apiClient.get<ApiResponse<QuizSetSummary[]>>('/quiz-sets'),
}
